import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/index";
import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import ActionBtn from "components/buttons/ActionBtn";
import { DraggableList } from "components/DraggableList/DraggableList";
import ConfirmTooltip from "components/confirmTooltip/ConfirmTooltip";
import binIcon from "assets/icons/bin-icon.svg";
import { FaqContentItem } from "store/cms/pages/faq/slice";
import styles from "../CmsPagesFaqEditModal.module.scss";

interface FaqItemDraft extends FaqContentItem {
  id: string;
}

interface Props {
  id: string;
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  readOnly?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
  onDraftChange: (draft: {
    geo: string;
    locale: string;
    items: FaqContentItem[];
  }) => void;
}

const PagesFaqItemsTab: React.FC<Props> = ({
  id,
  fieldErrors,
  clearFieldError,
  readOnly,
  onLoadingChange,
  onDraftChange,
}) => {
  const { t } = useTranslation();
  const localesList = useAppSelector((store) => store.settings.locales);
  const geoList = useAppSelector((store) => store.settings.geoList);
  const [geo, setGeo] = useState<string>("");
  const [locale, setLocale] = useState<string>("");
  const [items, setItems] = useState<FaqItemDraft[]>([]);
  const [isFetched, setIsFetched] = useState<boolean>(false);
  const [activeConfirmId, setActiveConfirmId] = useState<string | null>(null);
  const requestSeq = useRef(0);
  const nextItemId = useRef(1);
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;

  const createId = () => {
    const itemId = `faq-item-${nextItemId.current}`;
    nextItemId.current += 1;
    return itemId;
  };

  useEffect(() => {
    onLoadingChange?.(!isFetched);
  }, [isFetched, onLoadingChange]);

  useEffect(() => {
    if (!geo && geoList?.length) {
      setGeo(geoList[0]);
    }

    if (!locale && localesList?.length) {
      setLocale(localesList[0]);
    }
  }, [geo, locale, geoList, localesList]);

  useEffect(() => {
    if (!geo || !locale) {
      return;
    }

    const seq = ++requestSeq.current;
    setIsFetched(false);
    setActiveConfirmId(null);

    if (!id) {
      setItems([]);
      setIsFetched(true);
      return;
    }

    const loadTranslation = async () => {
      try {
        const result = await instance.get(
          `${apiRoutes.faqTranslation}/${id}?geo=${encodeURIComponent(geo)}&locale=${encodeURIComponent(locale)}`,
        );

        if (seq !== requestSeq.current) {
          return;
        }

        const rows = Array.isArray(result.data?.items) ? result.data.items : [];
        setItems(
          rows.map((row: FaqContentItem) => ({
            id: createId(),
            title: row?.title ?? "",
            description: row?.description ?? "",
          })),
        );
      } catch {
        if (seq !== requestSeq.current) {
          return;
        }

        setItems([]);
      } finally {
        if (seq === requestSeq.current) {
          setIsFetched(true);
        }
      }
    };

    loadTranslation();
  }, [geo, locale, id]);

  useEffect(() => {
    if (!isFetched || !geo || !locale) {
      return;
    }

    onDraftChangeRef.current({
      geo,
      locale,
      items: items.map(({ title, description }) => ({ title, description })),
    });
  }, [isFetched, geo, locale, items]);

  const updateItem = (
    itemId: string,
    field: keyof FaqContentItem,
    value: string,
  ) => {
    setItems((current) =>
      current.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item,
      ),
    );
    clearFieldError(field);
    clearFieldError("items");
  };

  if (!id) {
    return <div style={{ padding: 16 }}>{t("createEntityFirst")}</div>;
  }

  return (
    <div className={styles.faqTab}>
      <div className="cms-seo-tab mt-14">
        <div className="game-modal-tabs small">
          {geoList?.map((itemGeo) => (
            <div
              key={itemGeo}
              className={`tab-item ${geo === itemGeo ? "active" : ""}`}
              onClick={() => setGeo(itemGeo)}
            >
              {itemGeo.toLowerCase() === "ww" ? (
                <span className="mr-5">🌎</span>
              ) : (
                <img
                  src={`https://flagpedia.net/data/flags/icon/16x12/${itemGeo.toLowerCase()}.png`}
                  alt=""
                />
              )}
              {itemGeo}
            </div>
          ))}
        </div>
        <div className="game-modal-tabs small mt-14 mb-14">
          {localesList?.map((itemLocale) => (
            <div
              key={itemLocale}
              className={`tab-item ${locale === itemLocale ? "active" : ""}`}
              onClick={() => setLocale(itemLocale)}
            >
              <img
                src={`https://flagpedia.net/data/flags/icon/16x12/${itemLocale
                  .split("-")[1]
                  ?.toLowerCase()}.png`}
                alt=""
              />
              {t(itemLocale).replace(/-[a-z]+$/i, "")}
            </div>
          ))}
        </div>
      </div>

      {isFetched ? (
        <div className={styles.faqItems}>
          {!readOnly && (
            <div className={styles.faqAdd}>
              <ActionBtn
                txt={t("addFaqItem")}
                isWithText
                handleClick={() =>
                  setItems((current) => [
                    ...current,
                    { id: createId(), title: "", description: "" },
                  ])
                }
              />
            </div>
          )}

          {items.length > 0 && (
            <DraggableList
              items={items}
              selectedItems={[]}
              isDragRestricted={Boolean(readOnly)}
              onSort={setItems}
            >
              {items.map((item, index) => (
                <DraggableList.Item
                  key={item.id}
                  id={item.id}
                  index={index}
                  className={styles.faqItem}
                >
                  <div className={styles.faqItemRow}>
                    <div
                      className={styles.faqItemFields}
                      onMouseDown={(event) => event.stopPropagation()}
                    >
                      <InnerInput
                        id={`faq-item-title-${item.id}`}
                        value={item.title}
                        label={t("title")}
                        placeholder={t("enterTitle")}
                        type="text"
                        onChange={(value) => updateItem(item.id, "title", value)}
                        hasError={Boolean(fieldErrors?.["title"] || fieldErrors?.["items"])}
                        disabled={readOnly}
                      />
                      <InnerInput
                        id={`faq-item-description-${item.id}`}
                        value={item.description}
                        label={t("description")}
                        placeholder={t("enterDescription")}
                        type="textarea"
                        rows={3}
                        onChange={(value) =>
                          updateItem(item.id, "description", value)
                        }
                        hasError={Boolean(
                          fieldErrors?.["description"] || fieldErrors?.["items"],
                        )}
                        disabled={readOnly}
                      />
                    </div>
                    {!readOnly && (
                      <>
                        <img
                          src={binIcon}
                          alt={t("delete")}
                          className="delete-icon is-active"
                          data-tooltip-id={`faq-item-delete-${item.id}`}
                          onMouseDown={(event) => event.stopPropagation()}
                        />
                        <ConfirmTooltip
                          id={`faq-item-delete-${item.id}`}
                          title={t("areYouSureYouWantDeleteThisEntity")}
                          onConfirm={() =>
                            setItems((current) =>
                              current.filter((row) => row.id !== item.id),
                            )
                          }
                          isOpen={activeConfirmId === item.id}
                          onToggle={(open) =>
                            setActiveConfirmId(open ? item.id : null)
                          }
                          config={{
                            clickable: true,
                            openEvents: { click: true },
                            closeEvents: { click: true, blur: true },
                            position: "left",
                          }}
                        />
                      </>
                    )}
                  </div>
                </DraggableList.Item>
              ))}
            </DraggableList>
          )}
        </div>
      ) : (
        <ModalTabSkeleton />
      )}
    </div>
  );
};

export default PagesFaqItemsTab;
